import React, { useState, useRef, useEffect } from "react";
import { Search, User, Heart, ShoppingBag, Menu } from "lucide-react";
import MobileDrawer from "../components/MobileDrawer";
import AmbientSoundToggle from "../components/AmbientSoundToggle";
import { useClickOutside } from "../hooks/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { useWishlist } from "../hooks/useWishlist";
import { useSearch } from "../hooks/useSearch";
import { useNavAuth } from "../hooks/useNavAuth";
import { useTheme } from "../contexts/ThemeContext";
import { SearchSuggestion } from "../types/search.types";
import { sanitizeForUrl } from "../utils/urlSanitizer";
import { fetchCategories } from "../api/categoryApiService";
import { CategoryItem } from "../types/category.types";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store/store";
import { openAuthModal, openCartDrawer, openWishlistModal } from "../store/uiSlice";
import { handleAuthDialog } from "../utilities/authDialogIntegration";
import { setRedirectAfterLogin } from "../utilities/redirectUtils";
import "../customCSS/HeadNavBar.css";
import { getUserType } from "../utilities/cookieUtils";

const HeadNavBar = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  // Pure UI state — nothing from here touches business logic or APIs
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [categoriesMenu, setCategoriesMenu] = useState<CategoryItem[]>([]);
  const [menuLoading, setMenuLoading] = useState<boolean>(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isProfileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [isFooterOverlap, setIsFooterOverlap] = useState(false);


  // DOM refs — used by event listeners and useClickOutside
  const desktopSearchRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const headbarRef = useRef<HTMLElement | null>(null);
  const megaMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileSuggestionNavigatingRef = useRef(false);

  // Business logic delegated to hooks
  const search = useSearch();
  const navAuth = useNavAuth();
  const dispatch = useDispatch<AppDispatch>();

  // Local store counts (shown when user is not logged in via API)
  const { itemCount: localCartCount } = useCart();
  const { wishlistCount: localWishlistCount } = useWishlist();

  const navigate = useNavigate();

  const protectedPaths = ["/orders", "/order", "/address", "/giftcards"];

  const handleProtectedNavigation = (path: string) => {
    if (navAuth.isLoggedIn) {
      navigate(path);
      return;
    }

    if (protectedPaths.some((protectedPath) => path.startsWith(protectedPath))) {
      setRedirectAfterLogin(path);
      setProfileMenuOpen(false);
      handleAuthDialog(dispatch, navigate, {
        onAuthSuccess: () => {
          navigate(path);
        },
      });
      return;
    }

    navigate(path);
  };

  // Close desktop search suggestions when clicking outside
  useClickOutside(desktopSearchRef, search.closeSuggestions);
  // Close profile menu when clicking outside
  useClickOutside(profileMenuRef, () => setProfileMenuOpen(false));

  // Prevent body scroll when mega menu is open
  useEffect(() => {
    document.body.style.overflow = activeMenu ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [activeMenu]);

  // Close mega menu when pointer leaves both navbar and mega menu areas
  useEffect(() => {
    if (!activeMenu) return;

    const onPointerMove = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!navbarRef.current || !megaMenuRef.current) return;
      if (
        !navbarRef.current.contains(target) &&
        !megaMenuRef.current.contains(target)
      ) {
        setActiveMenu(null);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      const el = document.elementFromPoint(t.clientX, t.clientY);
      if (!navbarRef.current || !megaMenuRef.current) return;
      if (
        !navbarRef.current.contains(el as Node) &&
        !megaMenuRef.current.contains(el as Node)
      ) {
        setActiveMenu(null);
      }
    };

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("touchstart", onTouchStart);
    return () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("touchstart", onTouchStart);
    };
  }, [activeMenu]);

  // Fetch categories once; the API service shares the request/cache with other components.
  useEffect(() => {
    let cancelled = false;

    const fetchCategoriesOnce = async () => {
      setMenuLoading(true);
      try {
        const data = await fetchCategories();
        if (!cancelled) setCategoriesMenu(data);
      } catch (err) {
        console.error("Failed to fetch categories on mount:", err);
        if (!cancelled) setCategoriesMenu([]);
      } finally {
        if (!cancelled) setMenuLoading(false);
      }
    };

    void fetchCategoriesOnce();
    return () => { cancelled = true; };
  }, []);

  const goToPage = (path: string) => navigate(path);

  useEffect(() => {
    const footer = document.querySelector<HTMLElement>(".footer-header");
    if (!footer || !headbarRef.current) return;

    const checkFooterOverlap = () => {
      const headerRect = headbarRef.current?.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      if (!headerRect) return;

      const isOverlapping = footerRect.top < headerRect.bottom && footerRect.bottom > headerRect.top;
      setIsFooterOverlap(isOverlapping);
    };

    checkFooterOverlap();
    window.addEventListener("scroll", checkFooterOverlap, { passive: true });
    window.addEventListener("resize", checkFooterOverlap);

    return () => {
      window.removeEventListener("scroll", checkFooterOverlap);
      window.removeEventListener("resize", checkFooterOverlap);
    };
  }, []);

  // Top-level nav items (show nothing until categories load)
  const navFallback: string[] = [];

  // Track small/large screen to control search UI rendering
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleCloseMobileSearch = () => {
    mobileSuggestionNavigatingRef.current = false;
    setSearchOpen(false);
    search.clearSearch();
  };

  const handleMobileOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    handleCloseMobileSearch();
  };

  const handleSuggestionSelect = (suggestion: SearchSuggestion) => {
    search.onSuggestionClick(suggestion);
    setSearchOpen(false);
  };

  const getSuggestionProductRoute = (suggestion: SearchSuggestion): string | null => {
    const productSlug = sanitizeForUrl(suggestion.productName);
    const productCode = String(suggestion.code ?? "").trim();

    if (!productSlug || !productCode) return null;
    return `/product/${encodeURIComponent(productSlug)}/${encodeURIComponent(productCode)}`;
  };

  const handleMobileSuggestionSelect = (
    suggestion: SearchSuggestion,
    event: React.SyntheticEvent<HTMLElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();
    if (mobileSuggestionNavigatingRef.current) return;
    mobileSuggestionNavigatingRef.current = true;

    const productRoute = getSuggestionProductRoute(suggestion);

    if (!productRoute) {
      search.onSuggestionClick(suggestion);
      setSearchOpen(false);
      return;
    }

    search.clearSearch();
    setSearchOpen(false);
    navigate(productRoute);
  };

  // Helpers for category navigation (match HomePage behavior)
  const safeDecode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const buildCategoryRoute = (categoryName: string): string => {
    const slug = sanitizeForUrl(categoryName);
    const encodedCategory = encodeURIComponent(categoryName);
    return `/search/category/${slug}?category=${encodedCategory}`;
  };

  const openCategoryInNewTab = (categoryName: string) => {
    const categoryRoute = buildCategoryRoute(categoryName);
    // Use SPA navigation instead of opening a new tab
    navigate(categoryRoute);
  };

  const getCategoryNameFromItem = (item: unknown): string => {
    if (!item) return "";
    if (typeof item === "string") return String(item).trim();

    if (typeof item !== "object") return "";

    const category = item as CategoryItem;
    const displayName = String(category.name ?? category.title ?? "").trim();
    if (displayName) return displayName;

    const pageUrl = String(category.pageUrl ?? category.link ?? "").trim();
    if (!pageUrl) return "";

    const categoryPathMatch =
      pageUrl.match(/\/search\/category\/([^/?#]+)/i) ||
      pageUrl.match(/\/category\/([^/?#]+)/i);
    if (categoryPathMatch?.[1]) {
      return safeDecode(categoryPathMatch[1]).replace(/-/g, " ").trim();
    }

    return "";
  };

  const getMenuItemLabel = (item: CategoryItem) => {
    if (typeof item === "string") return item;
    return String(item?.title ?? item?.name ?? "");
  };

  const handleMenuCategoryClick = async (item: CategoryItem, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    // If item already contains a name/title, use it directly
    const nameFromItem = String(item?.name ?? item?.title ?? "").trim();
    if (nameFromItem) {
      openCategoryInNewTab(nameFromItem);
      setActiveMenu(null);
      return;
    }

    const derivedName = getCategoryNameFromItem(item);
    if (derivedName) {
      openCategoryInNewTab(derivedName);
      setActiveMenu(null);
      return;
    }

    // Fallback: try to fetch categories and match
    try {
      const categoryData = await fetchCategories();

      if (Array.isArray(categoryData) && categoryData.length) {
        const finalName = String(categoryData[0]?.name ?? categoryData[0]?.title ?? "");
        if (finalName) {
          openCategoryInNewTab(finalName);
          setActiveMenu(null);
          return;
        }
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  return (
    <header className={`head-navbar${isFooterOverlap ? " inverted" : ""}`} ref={headbarRef}>
      <div className="navbar-container" ref={navbarRef}>
        <div className="navbar-content">
          {/* Mobile Drawer Button */}
          <button
            type="button"
            className="mobile-menu-button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={24} />
          </button>

          {/* Logo */}
          <a href="/" className="group flex shrink-0 items-center gap-2 sm:gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gold/50 sm:h-8 sm:w-8">
              <span className="h-1.5 w-1.5 rounded-full bg-gold-bright" />
            </span>
            <span className="whitespace-nowrap font-serif text-base tracking-wide-luxe text-bone sm:text-xl dark:text-[#ffffff]">
              Sardar<span className={`gold-text font-semibold ${isDarkMode ? "dark:text-[#D4AF37]" : ""}`}>Times</span>94
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="nav-menu">
            {categoriesMenu && categoriesMenu.length > 0 ? (
              categoriesMenu.slice(0, 6).map((cat) => (
                <div
                  key={cat?.code ?? cat?.name}
                  className={`menu-item ${isDarkMode ? "dark-menu-item" : ""}`}
                  onClick={() => {
                    const name = String(cat?.name ?? cat?.title ?? "").trim();
                    if (name) {
                      openCategoryInNewTab(name);
                      setActiveMenu(null);
                    }
                  }}
                >
                  <button type="button" className="menu-button">
                    {cat?.name ?? cat?.title}
                  </button>
                </div>
              ))
            ) : (
              navFallback.map((menuItem) => (
                <div
                  key={menuItem}
                  className={`menu-item ${activeMenu === menuItem ? "active" : ""} ${isDarkMode ? "dark-menu-item" : ""}`}
                  onClick={() => setActiveMenu(activeMenu === menuItem ? null : menuItem)}
                >
                  <button type="button" className="menu-button">
                    {menuItem}
                  </button>
                </div>
              ))
            )}
          </nav>

          {/* Right Section */}
          <div className="navbar-right">
            {/* Search (show full input only on small screens) */}
            {isSmallScreen && (
              <div className="search-container" ref={desktopSearchRef}>
                <form className="search-box" onSubmit={search.onSubmit}>
                  <Search className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search for products, brands and more"
                    className="search-input"
                    value={search.searchQuery}
                    onChange={(e) => search.onQueryChange(e.target.value)}
                    onKeyDown={search.onKeyDown}
                  />
                </form>

                {/* Search Suggestions Dropdown */}
                {search.showSuggestions && search.suggestions.length > 0 && (
                  <div className="search-suggestions-dropdown">
                    {search.suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`search-suggestion-item${index === search.highlightedIndex
                          ? " suggestion-highlighted"
                          : ""
                          }`}
                        onClick={() => handleSuggestionSelect(suggestion)}
                      >
                        <Search className="suggestion-icon" size={16} />
                        <span className="suggestion-text">
                          {suggestion.productName}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Icons */}
            <div className="nav-icons">
              <AmbientSoundToggle />

              {/* Search button: show icon on desktop, full input on small screens */}
              {isSmallScreen ? (
                <div
                  className="icon-item mobile-search-button"
                  onClick={() => setSearchOpen(true)}
                >
                  <Search className="icon-svg" />
                  <span className="icon-label">Search</span>
                </div>
              ) : (
                <div
                  className="icon-item desktop-search-button"
                  onClick={() => setSearchOpen(true)}
                >
                  <Search className="icon-svg" />
                  <span className="icon-label">Search</span>
                </div>
              )}

              {/* Profile */}
              <div
                className="icon-item"
                ref={profileMenuRef}
                onClick={() => setProfileMenuOpen(!isProfileMenuOpen)}
              >
                <User className="icon-svg" />
                <span className="icon-label">Profile</span>

                {isProfileMenuOpen && (
                  <div
                    className="profile-menu"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="profile-menu-header">
                      <h4>Welcome</h4>
                      <p className="text-muted small mb-4">
                        To access account and manage orders
                      </p>

                      {navAuth.isLoggedIn ? (
                        <div className="d-flex flex-column">
                          <div className="mb-2 fw-semibold">
                            {navAuth.username}
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              setProfileMenuOpen(false);
                              await navAuth.logout();
                            }}
                          >
                            Logout
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="login-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setProfileMenuOpen(false);
                            dispatch(openAuthModal());
                          }}
                        >
                          LOGIN
                        </button>
                      )}
                    </div>

                    <a
                      href="/orders"
                      className="profile-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleProtectedNavigation("/orders");
                      }}
                    >
                      Orders
                    </a>
                    <a
                      href="/address"
                      className="profile-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleProtectedNavigation("/address");
                      }}
                    >
                      Address
                    </a>
                    <a
                      href="/wishlist"
                      className="profile-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        const userType = getUserType();
                        if(userType === "random") {
                          setProfileMenuOpen(false);
                          dispatch(openAuthModal());
                          return;
                        }
                        dispatch(openWishlistModal());
                        setProfileMenuOpen(false);
                      }}
                    >
                      Wishlist
                    </a>
                    <a
                      href="/giftcards"
                      className="profile-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        handleProtectedNavigation("/giftcards");
                      }}
                    >
                      Gift Cards
                    </a>
                    <a
                      href="/contact"
                      className="profile-menu-item"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/contact");
                        setProfileMenuOpen(false);
                      }}
                    >
                      Contact Us
                    </a>
                  </div>
                )}
              </div>

              {/* Wishlist */}
              <div
                className="icon-item position-relative d-flex flex-column align-items-center"
                onClick={() => {
                    const userType = getUserType();
                    if(userType === "random") {
                      dispatch(openAuthModal());
                      return;
                    }
                    dispatch(openWishlistModal());
                  }
                }
              >
                <Heart className="icon-svg icon-svg-large heart-icon" />
                <span className="icon-label">Wishlist</span>
                {navAuth.isLoggedIn ? (
                  navAuth.wishlistCount > 0 && (
                    <span className="badge position-absolute bg-danger rounded-pill top-0 start-100 translate-middle">
                      {navAuth.wishlistCount}
                    </span>
                  )
                ) : (
                  localWishlistCount > 0 && (
                    <span className="badge bg-danger rounded-pill position-absolute top-0 start-100 translate-middle">
                      {localWishlistCount}
                    </span>
                  )
                )}
              </div>

              {/* Cart */}
              <div
                className="icon-item position-relative d-flex flex-column align-items-center"
                onClick={() => {
                  const userType = getUserType();
                  if(userType === "random") {
                    dispatch(openAuthModal({ afterLogin: "openCart" }));
                    return;
                  }
                  dispatch(openCartDrawer());
                }
              }
              >
                <ShoppingBag className="icon-svg icon-svg-large" />
                <span className="icon-label">Bag</span>
                {navAuth.isLoggedIn ? (
                  navAuth.cartCount > 0 && (
                    <span className="badge bg-danger rounded-pill position-absolute top-0 start-100 translate-middle">
                      {navAuth.cartCount}
                    </span>
                  )
                ) : (
                  localCartCount > 0 && (
                    <span className="badge bg-danger rounded-pill position-absolute top-0 start-100 translate-middle">
                      {localCartCount}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mega Menu */}
      {activeMenu && (
        <div className="mega-menu" ref={megaMenuRef}>
          <div className="mega-menu-container">
            {activeMenu === "CATEGORIES" ? (
              menuLoading ? (
                <div className="p-3">Loading...</div>
              ) : categoriesMenu && categoriesMenu.length > 0 ? (
                <div className="mega-menu-grid">
                  {categoriesMenu.map((category, idx) => (
                    <div key={idx} className="category-column">
                      <h3 className="category-title">{category?.name ?? category?.title ?? "Category"}</h3>
                      <ul className="category-list">
                        <li className="category-item">
                          <button
                            type="button"
                            className="category-link btn btn-link p-0"
                            onClick={(e) => handleMenuCategoryClick(category, e)}
                          >
                            {getMenuItemLabel(category)}
                          </button>
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-muted">No categories found</div>
              )
            ) : activeMenu === "STUDIO" ? (
              <div className="studio-menu">
                <div className="studio-badge">New</div>
                <h2 className="studio-title">
                  Your daily inspiration for everything fashion
                </h2>
                <button type="button" className="studio-button">
                  Explore Studio
                </button>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Drawer */}
      <MobileDrawer
        open={isDrawerOpen}
        onClose={() => setDrawerOpen(false)}
        menuData={categoriesMenu}
        onNavigate={(path) => {
          if (path === "/login") {
            setDrawerOpen(false);
            dispatch(openAuthModal());
            return;
          }

          handleProtectedNavigation(path);
          setDrawerOpen(false);
        }}
      />

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="search-overlay" onClick={handleMobileOverlayClick}>
          <div
            className="search-overlay-content"
            onClick={(e) => e.stopPropagation()}
          >
            <Search className="search-icon" />
            <form onSubmit={search.onSubmit} className="search-overlay-form">
              <input
                type="text"
                placeholder="Search for products, brands and more"
                className="search-overlay-input"
                value={search.searchQuery}
                onChange={(e) => search.onQueryChange(e.target.value)}
                onKeyDown={search.onKeyDown}
                autoFocus
              />
            </form>
            <button
              type="button"
              className="search-overlay-close"
              aria-label="Close search"
              onClick={handleCloseMobileSearch}
            >
              ✕
            </button>
          </div>

          {/* Mobile Search Suggestions (shows results or a loading state) */}
          {(search.isSearching || (search.showSuggestions && search.suggestions.length > 0)) && (
            <div
              className="search-suggestions-dropdown-mobile"
              onClick={(e) => e.stopPropagation()}
            >
              {search.isSearching ? (
                <div className="search-loading" role="status" aria-live="polite">
                  <div className="spinner" aria-hidden="true" />
                  <span className="loading-text">Searching...</span>
                </div>
              ) : (
                search.suggestions.map((suggestion, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`search-suggestion-item${index === search.highlightedIndex
                      ? " suggestion-highlighted"
                      : ""
                      }`}
                    onPointerDown={(e) => handleMobileSuggestionSelect(suggestion, e)}
                    onClick={(e) => handleMobileSuggestionSelect(suggestion, e)}
                  >
                    <Search className="suggestion-icon" size={16} />
                    <span className="suggestion-text">
                      {suggestion.productName}
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default HeadNavBar;
